import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  TextField,
  Alert,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { CheckCircle } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { useAlerts, useAcknowledgeAlert } from '../../hooks/useRules'
import { format } from 'date-fns'

const SEVERITIES = ['', 'critical', 'warning', 'info']

export default function AlertsPage() {
  const { t } = useTranslation()
  const [severity, setSeverity] = useState('')
  const { data: alerts = [], isLoading } = useAlerts({
    limit: 100,
    severity: severity || undefined,
  })
  const acknowledge = useAcknowledgeAlert()

  const columns: GridColDef[] = [
    {
      field: 'severity',
      headerName: t('alerts.severity'),
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={
            value === 'critical' ? 'error' : value === 'warning' ? 'warning' : 'info'
          }
        />
      ),
    },
    { field: 'message', headerName: t('alerts.message'), flex: 1, minWidth: 200 },
    {
      field: 'device_id',
      headerName: t('nav.devices'),
      width: 200,
      renderCell: ({ value }) => (
        <Chip label={value ? `${String(value).slice(0, 12)}…` : '—'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'acknowledged',
      headerName: t('common.status'),
      width: 130,
      renderCell: ({ value }) => (
        <Chip
          label={value ? t('alerts.acknowledged') : t('common.active')}
          size="small"
          color={value ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('device.lastSeen'),
      width: 180,
      valueFormatter: (v: string) => (v ? format(new Date(v), 'PPp') : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: ({ row }) =>
        !row.acknowledged ? (
          <Tooltip title={t('alerts.acknowledge')}>
            <IconButton
              size="small"
              color="success"
              onClick={() => { acknowledge.mutate(row.id); analytics.track(EVENTS.ALERT_ACKNOWLEDGED, { alert_id: row.id, severity: row.severity, device_id: row.device_id }) }}
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ]

  return (
    <Box>
      <PageHeader
        title={t('alerts.title')}
        subtitle={t('alerts.filterSeverity')}
        breadcrumbs={[{ label: t('nav.ruleEngine') }, { label: t('alerts.title') }]}
        action={
          <TextField
            select
            size="small"
            label={t('alerts.severity')}
            value={severity}
            onChange={(e) => { setSeverity(e.target.value) }}
            sx={{ minWidth: 140 }}
          >
            {SEVERITIES.map((s) => (
              <MenuItem key={s} value={s}>
                {s || t('common.all')}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {acknowledge.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('alerts.acknowledge')} — {t('common.noData')}</Alert>
      )}

      <DataGrid
        rows={alerts}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
    </Box>
  )
}
